import { Upload, FileText, LoaderCircle } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"

import { ShimmerText } from "@/components/indicators/TypingIndicator"
import { Button } from "@ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@ui/dialog"
import { useUserSession } from "@session/UserSession"

const REPORT_TEMPLATE_TYPE_ID = "687ea5acaecd239e450bc9f6"

interface UploadTemplateButtonProps {
    configurationId: string | undefined
    onUploadDone: (documentId: string) => void
}
export function UploadTemplateButton({ configurationId, onUploadDone }: Readonly<UploadTemplateButtonProps>) {
    const { client } = useUserSession()

    const [open, setOpen] = useState<boolean>(false)
    const [file, setFile] = useState<File | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleButtonClick = () => {
        fileInputRef.current?.click()
    }

    const handleFileSelect = (event: any) => {
        if (event.target.files[0]) {
            setFile(event.target.files[0])
            setOpen(true)
        }
    }

    const uploadGuideline = async () => {
        if (!file) {
            return
        }

        client.objects.create({ type: REPORT_TEMPLATE_TYPE_ID, content: file })
            .then((response) => {
                addTemplateToConfiguration(response.id)
            })
            .catch((error: any) => {
                toast.error("Upload Failed", {
                    description: error.message,
                    duration: 4000
                })
            })
            .finally(() => {
                handleClose()
            })
    }

    const addTemplateToConfiguration = (documentId: string) => {
        if (!client || !configurationId || !documentId) {
            return
        }

        client.store.collections.addMembers(configurationId, [documentId])
            .then(() => {
                toast.success("Upload Successful", {
                    description: "Template uploaded successfully",
                    duration: 4000
                })

                onUploadDone(documentId)
            })
            .catch((error: any) => {
                toast.error("Failed to add template to configuration", {
                    description: error.message,
                    duration: 4000
                })
            })
            .finally(() => {
                handleClose()
            })
    }

    const handleClose = () => {
        setFile(null)
        setOpen(false)
        if (fileInputRef.current) {
            fileInputRef.current.value = ""
        }
    }

    useEffect(() => {
        if (!file) {
            return
        }

        uploadGuideline()
    }, [file])

    return (
        <>
            <Button onClick={handleButtonClick} variant={"outline"} className="text-blue-400 border-blue-400 hover:border-blue-600 hover:text-blue-600" disabled={!configurationId}>
                <Upload />
                Upload Template
            </Button>

            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
            />

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Uploading Document</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4 w-full">
                        {
                            file && (
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                    <FileText className="w-8 h-8 text-blue-600" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {file.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {(file.size / 1024 / 1024).toFixed(2)} MO
                                        </p>
                                    </div>
                                </div>
                            )
                        }
                        <ShimmerText>
                            <div className="flex w-full items-center justify-center gap-2">
                                <LoaderCircle className="animate-spin motion-safe:animate-[spin_2s_linear_infinite]" />
                                <span>Upload in progress. Please wait.</span>
                            </div>
                        </ShimmerText>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}